import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { GradientMesh } from '@/components/ui/GradientMesh'
import { motion } from 'framer-motion'

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)
  const { changePassword } = useAppStore()
  const navigate = useNavigate()

  // Supabase sends the user back here with an access token in the URL hash
  // The onAuthStateChange listener in initAuth will pick up the SIGNED_IN event
  // and set the session, so changePassword will work immediately.

  async function handleSubmit(e) {
    e.preventDefault()
    if (!password.trim()) return
    if (password !== confirm) { setError('Passwords do not match'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    setLoading(true)
    setError(null)
    const { error: err } = await changePassword(password)
    setLoading(false)
    if (err) { setError(err.message); return }
    setDone(true)
    setTimeout(() => navigate('/'), 2000)
  }

  return (
    <div className="min-h-dvh bg-bg flex items-center justify-center p-4 relative overflow-hidden">
      <GradientMesh />
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm bg-surface border border-border rounded-2xl p-8 relative z-10"
      >
        <div className="mb-6 text-center">
          <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center text-accent font-bold text-sm mx-auto mb-4">P</div>
          <h1 className="text-xl font-semibold text-text">Set new password</h1>
          <p className="text-sm text-text-muted mt-1">Choose a strong password for your account.</p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4"
          >
            <p className="text-sm font-medium text-text">Password updated!</p>
            <p className="text-xs text-text-muted mt-1">Redirecting you in…</p>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-xs text-text-muted mb-1.5 block">New password</label>
              <Input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
              />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1.5 block">Confirm password</label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat password"
              />
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs text-red-400"
              >
                {error}
              </motion.p>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full mt-1"
              disabled={loading || !password || !confirm}
            >
              {loading ? 'Saving…' : 'Set password'}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  )
}
